import React, { useState, useRef, useEffect } from 'react';
import './SearchableSelect.css';

/**
 * Searchable select dropdown component.
 *
 * Props:
 *   options: [{ id, label }]
 *   value: selected id (string or number)
 *   onChange: (id, option) => void
 *   placeholder: string
 *   emptyLabel: string (label for the "none" option)
 *   allowEmpty: boolean (default true)
 *   onSearch: async (query) => [{ id, label }]
 *     Když je zadané, filtruje se na serveru (debounce 300 ms) místo lokálně —
 *     pro číselníky, které jsou moc velké na předběžné načtení (např. contact_person).
 *   selectedLabel: string (popisek vybrané položky, když není v `options`)
 */
function SearchableSelect({ options = [], value, onChange, placeholder = 'Hledat…', emptyLabel = '— žádný —', allowEmpty = true, onSearch = null, selectedLabel = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Find selected option label
  const selectedOption = options.find(o => String(o.id) === String(value));
  const displayValue = selectedOption ? selectedOption.label : (selectedLabel || '');

  // Filter options by search
  const filtered = onSearch
    ? remoteOptions
    : (search ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())) : options);

  // Server-side hledání s debounce
  useEffect(() => {
    if (!onSearch || !isOpen) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await onSearch(search);
        if (!cancelled) setRemoteOptions(result || []);
      } catch (err) {
        if (!cancelled) setRemoteOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search, isOpen, onSearch]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Výška dropdownu z CSS (.searchable-select-dropdown max-height)
  const DROPDOWN_HEIGHT = 280;

  const handleOpen = () => {
    // Když je select u spodního okraje okna, rozbal ho nahoru — jinak by se
    // nabídka ořízla o konec stránky (typicky poslední pole ve formuláři).
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setDropUp(spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow);
    }
    setIsOpen(true);
    setSearch('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelect = (id, option = null) => {
    onChange(id, option);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <div
        className={`searchable-select-trigger form-control ${isOpen ? 'active' : ''}`}
        onClick={handleOpen}
      >
        {displayValue || <span className="text-muted">{emptyLabel}</span>}
        <i className="bi bi-chevron-down searchable-select-arrow"></i>
      </div>

      {isOpen && (
        <div className={`searchable-select-dropdown${dropUp ? ' drop-up' : ''}`}>
          <div className="searchable-select-search">
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-sm"
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="searchable-select-options">
            {allowEmpty && (
              <div
                className={`searchable-select-option ${!value ? 'selected' : ''}`}
                onClick={() => handleSelect('')}
              >
                {emptyLabel}
              </div>
            )}
            {loading ? (
              <div className="searchable-select-no-results">Načítám…</div>
            ) : filtered.length === 0 ? (
              <div className="searchable-select-no-results">Žádné výsledky</div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.id}
                  className={`searchable-select-option ${String(opt.id) === String(value) ? 'selected' : ''}`}
                  onClick={() => handleSelect(opt.id, opt)}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
